"use client";

import { BarChart3 } from "lucide-react";

import type { PremiumSlice } from "./PremiumPiePanel";

const money = (n: number) => `$${Math.round(n).toLocaleString()}`;

/**
 * Per-sub-agency scorecard — policies, agents, average policy size, closing
 * ratio and premium per agent, side by side.
 *
 * The donut above answers "who produced how much"; this answers "how well".
 * Two agencies can contribute the same premium with very different headcount
 * and close rates, and that difference is invisible in a pie.
 */
export function SubAgencyScorecardPanel({ slices }: { slices: PremiumSlice[] }) {
  const rows = slices
    .filter((s) => s.premium > 0 || (s.policies || 0) > 0)
    .sort((a, b) => b.premium - a.premium);

  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <p className="text-sm font-semibold text-gray-800">Sub-agency scorecard</p>
        <p className="py-4 text-center text-sm text-muted-foreground">
          No sub-agencies beneath this one.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-md bg-violet-50 text-violet-600">
          <BarChart3 className="h-3.5 w-3.5" />
        </span>
        <div>
          <p className="text-sm font-semibold text-gray-800">Sub-agency scorecard</p>
          <p className="text-[11px] text-muted-foreground">Output per team, not just totals</p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b text-[10px] uppercase tracking-wide text-muted-foreground">
              <th className="pb-1.5 text-left font-medium">Agency</th>
              <th className="pb-1.5 text-right font-medium">Policies</th>
              <th className="pb-1.5 text-right font-medium">Agents</th>
              <th className="pb-1.5 text-right font-medium">Avg policy</th>
              <th className="pb-1.5 text-right font-medium">Per agent</th>
              <th className="pb-1.5 text-right font-medium">Close</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const agents = r.agents || 0;
              // Premium per agent is the cleanest productivity signal available
              // here — a big team producing the same as a small one is doing
              // materially worse, and the totals alone hide that.
              const perAgent = agents > 0 ? r.premium / agents : 0;
              return (
                <tr key={r.id} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="max-w-[150px] truncate py-2 pr-2 text-gray-700">{r.name}</td>
                  <td className="py-2 text-right tabular-nums text-gray-900">
                    {(r.policies || 0).toLocaleString()}
                  </td>
                  <td className="py-2 text-right tabular-nums text-gray-900">{agents}</td>
                  <td className="py-2 text-right tabular-nums text-gray-900">
                    {money(r.averagePolicySize || 0)}
                  </td>
                  <td className="py-2 text-right font-medium tabular-nums text-violet-600">
                    {agents > 0 ? money(perAgent) : "—"}
                  </td>
                  <td className="py-2 text-right tabular-nums text-gray-500">
                    {(r.closingRatio || 0).toFixed(1)}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
